/* A Leading Role - Concierge bridge
   Routes the existing chat widget to the live Concierge worker.

   alr.js is wrapped in an IIFE, so none of its functions are reachable from
   outside. This intercepts the form submit on `document` in the CAPTURE phase,
   which runs before any listener bound to the form, so the canned path never
   fires.

   IMPORTANT: alr.js keeps the transcript in sessionStorage as
   [{ text, who }] under alr_concierge_history_<customer-or-guest>, and
   re-renders from it whenever the panel opens. Writing only to the DOM meant
   messages were replayed over, or lost on navigation. So that store is the
   single source of truth here: every exchange is written to it, and the panel
   is rebuilt from it on open.

   If the worker is unreachable the listener removes itself and the original
   canned concierge takes over, so a customer always gets an answer. */
(function () {
  'use strict';

  var ENDPOINT = '/apps/concierge/chat';
  var MAX_TURNS = 8;
  var busy = false;

  function msgs() { return document.getElementById('c-msgs'); }

  function historyKey() {
    var id = '';
    try {
      var el = document.getElementById('c-customer-data');
      if (el) id = (JSON.parse(el.textContent || '{}') || {}).id || '';
    } catch (e) { /* guest */ }
    return 'alr_concierge_history_' + (id || 'guest');
  }

  function load() {
    try { return JSON.parse(sessionStorage.getItem(historyKey()) || '[]') || []; }
    catch (e) { return []; }
  }

  function save(list) {
    try { sessionStorage.setItem(historyKey(), JSON.stringify(list.slice(-40))); }
    catch (e) { /* private mode */ }
  }

  function remember(text, who) {
    var list = load();
    var last = list[list.length - 1];
    if (last && last.text === text && last.who === who) return; // no double entries
    list.push({ text: text, who: who });
    save(list);
  }

  // Turn bare URLs into links the way a person expects.
  /* Keeping the newest message in view --------------------------------
     Product thumbnails have no height at the moment their bubble is
     appended, so scrollHeight is short and the panel lands above the new
     message; every picture that then loads pushes it further out of sight.
     Re-pin to the bottom as each image settles. */
  function stick() {
    var box = msgs();
    if (box) box.scrollTop = box.scrollHeight;
  }

  function stickThrough(el) {
    stick();
    var imgs = (el && el.querySelectorAll) ? el.querySelectorAll('img') : [];
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) continue;
      imgs[i].addEventListener('load', stick);
      imgs[i].addEventListener('error', stick);
    }
    setTimeout(stick, 150);
  }

  function paint(el, text) {
    el.textContent = '';
    var parts = String(text).split(/(https?:\/\/[^\s)]+)/g);
    for (var i = 0; i < parts.length; i++) {
      if (/^https?:\/\//.test(parts[i])) {
        var a = document.createElement('a');
        a.href = parts[i];
        a.textContent = parts[i].replace(/^https?:\/\//, '');
        a.style.textDecoration = 'underline';
        el.appendChild(a);
      } else if (parts[i]) {
        el.appendChild(document.createTextNode(parts[i]));
      }
    }
  }

  // alr.js writes ITS messages into this same store as HTML - the greeting
  // carries entities and a sign-in <a> tag. Worker replies are plain text.
  // The html flag says which is which, so neither gets mangled: flagged
  // entries go back in as HTML, unflagged worker text stays text and only
  // has its bare URLs linkified.
  /* Product cards ------------------------------------------------------
     The worker answers in prose and usually names a product without linking
     it, so a reply lands as a wall of text where the old canned concierge
     used to show a picture. The catalogue feed at /pages/concierge-kb (the
     same one alr.js reads, under the same cache key, so this costs no extra
     fetch) carries every product's title, url, price and thumbnail. Any
     product the reply names by title, or links by handle, gets a card
     appended under the bubble - built only from feed data and escaped,
     never from the model's own words. */
  var KB = null;
  var KB_KEY = 'alr_concierge_kb';

  function kbPage(n) {
    return fetch('/pages/concierge-kb?page=' + n, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function kb(cb) {
    if (KB) { cb(KB); return; }
    try {
      var cached = sessionStorage.getItem(KB_KEY);
      if (cached) { KB = JSON.parse(cached); cb(KB); return; }
    } catch (e) { /* private mode */ }
    kbPage(1).then(function (first) {
      if (!first || !first.products) throw new Error('bad kb');
      var pages = parseInt(first.pages, 10) || 1;
      if (pages < 2) return first;
      var rest = [];
      for (var n = 2; n <= pages; n++) { rest.push(kbPage(n)); }
      return Promise.all(rest).then(function (others) {
        others.forEach(function (o) {
          if (o && o.products && o.products.length) {
            first.products = first.products.concat(o.products);
          }
        });
        return first;
      });
    }).then(function (data) {
      KB = data;
      try { sessionStorage.setItem(KB_KEY, JSON.stringify(data)); } catch (e) {}
      cb(KB);
    }).catch(function () { cb(null); });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* Which products is this reply talking about? -------------------------
     Two passes. If the worker names a product in full, or links its handle,
     that is unambiguous and we use only those. But it often writes the way a
     person would - "the Signature Mermaid Child Transformation Skirt in five
     colorways - aqua, pink, purple..." - which names no title in full. So the
     second pass asks, for each product, whether EVERY distinctive word of its
     title appears somewhere in the reply. Brand and category filler is
     stripped first, or every product would match every answer. */
  var FILLER = /^(the|and|with|for|role|leading|signature|premium|child|childs|children|adult|dress|kids|our|new|accessory|accessories|collection|exclusive|officially|licensed)$/;

  function tokens(title) {
    return String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ')
      .trim().split(' ')
      .filter(function (w) { return w.length > 2 && !FILLER.test(w); });
  }

  function matches(data, text) {
    if (!data || !data.products) return [];
    var low = String(text).toLowerCase();
    var exact = [];
    var fuzzy = [];

    data.products.forEach(function (p) {
      if (!p.t || !p.u) return;
      var handle = (p.u.split('/products/')[1] || '').toLowerCase();
      var at = low.indexOf(p.t.toLowerCase());
      var linkAt = handle ? low.indexOf('/products/' + handle) : -1;
      if (at !== -1 || linkAt !== -1) {
        exact.push({ p: p, at: at === -1 ? linkAt : at, tk: tokens(p.t) });
        return;
      }
      var tk = tokens(p.t);
      if (!tk.length) return;
      var positions = [];
      for (var i = 0; i < tk.length; i++) {
        var idx = low.indexOf(tk[i]);
        if (idx === -1) return;              // a distinctive word is missing
        positions.push(idx);
      }
      fuzzy.push({ p: p, tk: tk, positions: positions });
    });

    // Exact hits are certain, so they are never dropped - but a reply often
    // names one product in full and another loosely ("the Werewolf Child Dress
    // Up ... there's also the Red Riding Hood costume"), and the loose one
    // deserves a card too. Keep both, with exact taking precedence on any
    // product matched twice.
    var exactIds = {};
    exact.forEach(function (x) { exactIds[x.p.u] = 1; });
    fuzzy = fuzzy.filter(function (m) { return !exactIds[m.p.u]; });
    if (!fuzzy.length) {
      return exact.sort(function (a, b) { return a.at - b.at; })
                  .slice(0, 5)
                  .map(function (x) { return x.p; });
    }

    // "Ladybug" is a strict subset of "Ladybug 10th Anniversary", so an answer
    // about the anniversary set would otherwise pull in the plain one too.
    // Keep only the most specific match of any such pair.
    // "Ladybug" is a strict subset of "Ladybug 10th Anniversary". Compare
    // against the exact matches too, or naming the anniversary set in full
    // would drag the plain one in behind it.
    var rivals = fuzzy.concat(exact);
    var kept = fuzzy.filter(function (a) {
      return !rivals.some(function (b) {
        if (a === b || !b.tk || b.tk.length <= a.tk.length) return false;
        return a.tk.every(function (w) { return b.tk.indexOf(w) !== -1; });
      });
    });

    // Order by where each one is actually discussed. A word unique to a single
    // match (the colour, in a list of colourways) locates it far better than a
    // word every match shares.
    var freq = {};
    kept.forEach(function (m) {
      m.tk.forEach(function (w) { freq[w] = (freq[w] || 0) + 1; });
    });
    kept.forEach(function (m) {
      var own = [];
      for (var i = 0; i < m.tk.length; i++) {
        if (freq[m.tk[i]] === 1) own.push(m.positions[i]);
      }
      m.at = own.length ? Math.min.apply(null, own) : Math.min.apply(null, m.positions);
    });

    return kept.concat(exact)
               .sort(function (a, b) { return a.at - b.at; })
               .slice(0, 5)
               .map(function (x) { return x.p; });
  }

  function attachCards(el, text) {
    if (!el || !text) return;
    kb(function (data) {
      if (!el.parentNode) return;
      if (el.querySelector('.c-prod')) return;   // already carded
      var hits = matches(data, text);
      if (!hits.length) { stick(); return; }
      hits.forEach(function (p) {
        var a = document.createElement('a');
        a.className = 'c-prod';
        a.href = p.u;
        a.innerHTML =
          (p.img ? '<img src="' + esc(p.img) + '" alt="">' : '') +
          '<span>' + esc(p.t) +
          (p.price ? ' &mdash; ' + esc(p.price) : '') +
          (p.avail === false ? ' &middot; sold out' : '') +
          '</span>';
        el.appendChild(a);
      });
      stickThrough(el);
    });
  }

  function render(el, entry) {
    if (entry && entry.html) { el.innerHTML = entry.text; return; }
    paint(el, entry ? entry.text : '');
    if (entry && entry.who !== 'me') attachCards(el, entry.text);
  }

  function bubble(text, mine) {
    var box = msgs();
    if (!box) return null;
    var el = document.createElement('div');
    el.className = 'c-msg ' + (mine ? 'me' : 'bot');
    paint(el, text);
    box.appendChild(el);
    stickThrough(el);
    return el;
  }

  // Redraw the whole panel from the store, collapsing any repeats alr.js
  // may have appended.
  function rebuild() {
    var box = msgs();
    if (!box) return;
    var list = load();
    if (!list.length) return;               // leave the greeting alone
    var clean = [];
    for (var i = 0; i < list.length; i++) {
      var prev = clean[clean.length - 1];
      if (prev && prev.text === list[i].text && prev.who === list[i].who) continue;
      clean.push(list[i]);
    }
    box.innerHTML = '';
    for (var j = 0; j < clean.length; j++) {
      var el = document.createElement('div');
      el.className = 'c-msg ' + (clean[j].who === 'me' ? 'me' : 'bot');
      render(el, clean[j]);
      box.appendChild(el);
    }
    stickThrough(box);
  }

  function turns() {
    return load()
      .filter(function (m) { return m.who === 'me' || m.who === 'bot'; })
      .slice(-MAX_TURNS)
      .map(function (m) {
        return { role: m.who === 'me' ? 'user' : 'assistant', content: m.text };
      });
  }

  function ask(text) {
    bubble(text, true);
    remember(text, 'me');
    var thinking = bubble('…', false);

    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ message: text, history: turns() })
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.reply) throw new Error('empty');
        if (thinking) {
          paint(thinking, d.reply);
          stickThrough(thinking);
          attachCards(thinking, d.reply);
        }
        remember(d.reply, 'bot');
      })
      .catch(function () {
        var msg = "I can't reach our system this moment. Email shop@aleadingrole.com and the team will help you straight away.";
        if (thinking) { paint(thinking, msg); stickThrough(thinking); }
        remember(msg, 'bot');
      });
  }

  function onSubmit(ev) {
    var input = document.getElementById('c-input');
    if (!input) return;
    var form = input.closest('form');
    if (!form || ev.target !== form) return;

    ev.preventDefault();
    ev.stopPropagation();

    var text = (input.value || '').trim();
    if (!text || busy) return;

    input.value = '';
    // alr.js persists the unsent draft so it survives navigation. This
    // listener pre-empts its submit handler, so clear the draft here too,
    // or a sent message reappears in the box on the next page.
    try { sessionStorage.removeItem('alr_concierge_draft'); } catch (e) {}
    busy = true;
    ask(text).then(function () { busy = false; });
  }

  document.addEventListener('submit', onSubmit, true);

  // alr.js re-renders from its store when the panel opens. Let it finish,
  // then redraw from the same store so nothing is duplicated or dropped.
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (t && t.closest && t.closest('#concierge-btn')) setTimeout(rebuild, 350);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(rebuild, 900); });
  } else {
    setTimeout(rebuild, 900);
  }

  // Stand down if the worker is unreachable, leaving the canned concierge.
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ message: '' })
  }).then(function (r) {
    if (r.status !== 400 && r.status !== 200) throw new Error('bad status');
  }).catch(function () {
    document.removeEventListener('submit', onSubmit, true);
  });
})();
